// frontend/src/components/onboarding/stage4-measurement.tsx
//
// Drop-in replacement. Now gates on customer.framework_approved at the top.

import React, { useCallback, useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from "react";
import {
  Check, AlertTriangle, Loader2, RotateCcw, StopCircle, Clock, Lock, Rocket,
  Play, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";

import { getCustomer, type CustomerDetail } from "@/api/onboarding-api";
import {
  BatchConfigPicker,
  computeBatchCalls,
  computeBatchCost,
  toCount,
  type BatchConfig,
} from "./onboarding-batch-config";
import {
  MEASUREMENT_MODELS,
  MEASUREMENT_DEFAULT_MODELS,
  getMeasurementLaneOption,
  resolveMeasurementWebSearch,
  type MeasurementModel,
} from "@/app/constants/models";
import {
  fetchMeasurementState,
  fetchMeasurementVersionPolicy,
  startMeasurement,
  cancelMeasurement,
  resumeMeasurement,
  openMeasurementWs,
  type AiModeRetrySummary,
  type MeasurementRun,
  type MeasurementVersionPolicy,
  type MeasurementWsMessage,
} from "@/api/onboarding-measurement-api";
import { useResumableWs } from "./hooks/use-resumable-ws";
import { SmokeCalibrationPanel } from "./smoke-calibration-panel";

// A smoke_calibration run is a pre-flight parked outside the baseline lineage —
// it must never drive the main Stage-4 measurement UI (progress panel, "Continue
// to Stage 5", WS stage-advance). Treat it as "no baseline run yet"; the
// SmokeCalibrationPanel owns its own lifecycle.
function asBaselineRun(run: MeasurementRun | null): MeasurementRun | null {
  return run && run.run_label === "smoke_calibration" ? null : run;
}

function laneLabel(lane: string): string {
  return getMeasurementLaneOption(lane)?.label ?? lane;
}

// Per-lane grounding the server will resolve for this config, mirroring
// _validate_and_normalize_batch_config: the operator's override where given,
// else the lane's catalog default. Shown before the run starts so the summary
// states the regime the run will actually execute, not just the button states.
function fmtGroundingPlan(cfg: BatchConfig): string {
  const lanes = cfg.models ?? [];
  if (lanes.length === 0) return "";
  const web: string[] = [];
  const stat: string[] = [];
  for (const lane of lanes) {
    (resolveMeasurementWebSearch(lane, cfg.webSearchOverrides) ? web : stat)
      .push(laneLabel(lane));
  }
  if (stat.length === 0) return "All lanes web-grounded.";
  if (web.length === 0) return "All lanes static.";
  return `Web-grounded: ${web.join(", ")}. Static: ${stat.join(", ")}.`;
}

// Same summary for a run that already exists, read off the server-resolved map
// the run was started with. "" for legacy runs that predate the field.
function fmtGroundingActual(ws?: Record<string, boolean> | null): string {
  if (!ws || typeof ws !== "object") return "";
  const lanes = Object.keys(ws);
  if (lanes.length === 0) return "";
  const web = lanes.filter((l) => ws[l]).map(laneLabel);
  const stat = lanes.filter((l) => !ws[l]).map(laneLabel);
  if (stat.length === 0) return "All lanes web-grounded";
  if (web.length === 0) return "All lanes static";
  return `Web: ${web.join(", ")} · Static: ${stat.join(", ")}`;
}

interface Props {
  customer: CustomerDetail;
  onCustomerUpdate: (c: CustomerDetail) => void;
  onStageComplete: () => void;
  autoStart: boolean;
}

export function Stage4Measurement({
  customer,
  onCustomerUpdate,
  onStageComplete,
  autoStart,
}: Props) {
  const totalQueries = customer.framework?.queries?.length ?? 25;

  const [run, setRun] = useState<MeasurementRun | null>(null);
  const [loading, setLoading] = useState(true);
  const [batchConfig, setBatchConfig] = useState<BatchConfig>({
    queries: Math.min(5, totalQueries),
    reps: 2,
    models: [...MEASUREMENT_DEFAULT_MODELS],
  });
  // Which model versions this customer's next run defaults to, resolved
  // server-side. A client with no baseline of its own starts on "Current" and
  // keeps the versions its first read pinned; a client anchored to the imported
  // baseline comes back "baseline" with no pins, exactly as before. null until
  // it loads — the picker falls back to its own "baseline" default meanwhile.
  const [versionPolicy, setVersionPolicy] = useState<MeasurementVersionPolicy | null>(null);
  const [starting, setStarting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [showNewRun, setShowNewRun] = useState(false);
  // Full-baseline go/no-go gate — opened by the Smoke calibration panel (PASS or
  // acknowledged REVIEW). Until then the Execute control below is disabled.
  const [calibrationGateOpen, setCalibrationGateOpen] = useState(false);

  const [livePct, setLivePct] = useState(0);
  const [liveCompleted, setLiveCompleted] = useState(0);
  const [liveTotal, setLiveTotal] = useState(0);
  const [liveCost, setLiveCost] = useState(0);

  // Timestamp of the last WS frame. A socket can stay OPEN but go silent (the
  // server finished but the final frame never arrived), which used to freeze the
  // bar at its last value forever — onClose was the only reconciler. We poll the
  // run endpoint when the socket has been quiet past a threshold; server wins.
  const lastWsAtRef = useRef<number>(Date.now());

  // ⚠️ Gate everything below on framework approval. Without this, the user
  // could navigate to Stage 4 (if current_stage is already 4 from a prior
  // approval) and press Execute even after going back to edit the framework
  // without re-approving. Keep the API guard too — this is just UX.
  const frameworkApproved = customer.framework_approved === true;

  // After a WS drop, the server-side run may have finished while the
  // client was disconnected. Poll Postgres once for the authoritative
  // state so the UI doesn't sit on a stale "running" view forever.
  const recheckIfMaybeFinished = useCallback(async () => {
    try {
      const res = await fetchMeasurementState(customer.id);
      const activeRun = asBaselineRun(res.run);
      if (!activeRun) return;
      setRun(activeRun);
      setLiveCompleted(activeRun.progress_completed);
      setLiveTotal(activeRun.progress_total);
      setLiveCost(activeRun.cost_so_far_usd);
      setLivePct(
        activeRun.progress_total > 0
          ? (activeRun.progress_completed / activeRun.progress_total) * 100
          : 0,
      );
      if (activeRun.status === "completed") {
        toast.success("Measurement finished while the page was disconnected.");
        onStageComplete();
      }
    } catch {
      /* network blip; user can refresh */
    }
  }, [customer.id, onStageComplete]);

  // ── On mount: fetch state. WS attach/detach is now driven by the
  //    useResumableWs hook below off the run's lifecycle status. ──────
  useEffect(() => {
    if (!frameworkApproved) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      // The version policy decides which versions the run will resolve, so it
      // is fetched here and seeded into the picker rather than left to the
      // picker's own "baseline" fallback — the operator must see the versions
      // that will actually run before pressing Execute, not after.
      try {
        const policy = await fetchMeasurementVersionPolicy(customer.id);
        if (!cancelled) {
          setVersionPolicy(policy);
          // Seed the toggle, but never overwrite a choice the operator has
          // already made (this effect also re-runs on customer change).
          setBatchConfig((prev) =>
            prev.versionMode ? prev : { ...prev, versionMode: policy.default_mode },
          );
        }
      } catch {
        /* Non-fatal: the picker falls back to its own default and the server
           still resolves the customer's real default on submit. */
      }
      try {
        const res = await fetchMeasurementState(customer.id);
        if (cancelled) return;
        // A smoke_calibration run must never drive the main Stage-4 view — it
        // belongs to the SmokeCalibrationPanel. Without this guard it leaks in
        // as "Run #-1 / Run complete" and hides the calibration panel.
        const activeRun = asBaselineRun(res.run);
        setRun(activeRun);

        if (activeRun) {
          setLiveCompleted(activeRun.progress_completed);
          setLiveTotal(activeRun.progress_total);
          setLiveCost(activeRun.cost_so_far_usd);
          setLivePct(
            activeRun.progress_total > 0
              ? (activeRun.progress_completed / activeRun.progress_total) * 100
              : 0,
          );
        }
      } catch (e: any) {
        toast.error(e?.message || "Failed to load measurement state");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [customer.id, frameworkApproved]);

  const handleWsMessage = useCallback((msg: MeasurementWsMessage) => {
    // Any frame means the socket is alive — reset the silence clock.
    lastWsAtRef.current = Date.now();
    if (msg.type === "snapshot") {
      // Reconcile to the server snapshot on (re)connect, but never let a
      // smoke_calibration run take over the main view (see asBaselineRun).
      const activeRun = asBaselineRun(msg.run);
      setRun(activeRun);
      if (activeRun) {
        setLiveCompleted(activeRun.progress_completed);
        setLiveTotal(activeRun.progress_total);
        setLiveCost(activeRun.cost_so_far_usd);
        setLivePct(
          activeRun.progress_total > 0
            ? (activeRun.progress_completed / activeRun.progress_total) * 100
            : 0,
        );
      }
    } else if (msg.type === "measurement_started") {
      setLiveTotal(msg.total);
      if (msg.resumed) {
        const c = msg.completed ?? 0;
        setLiveCompleted(c);
        setLiveCost(msg.cost_so_far ?? 0);
        setLivePct(msg.total > 0 ? (c / msg.total) * 100 : 0);
        toast.info(`Resuming from ${c} / ${msg.total} calls.`);
      }
    } else if (msg.type === "measurement_progress") {
      setLiveCompleted(msg.completed);
      setLiveTotal(msg.total);
      setLivePct(msg.percent);
      setLiveCost(msg.cost_so_far);
      setRun((prev) =>
        prev ? {
          ...prev,
          status: "running",
          progress_completed: msg.completed,
          progress_total: msg.total,
          cost_so_far_usd: msg.cost_so_far,
        } : prev,
      );
    } else if (msg.type === "measurement_complete") {
      setLivePct(100);
      setLiveCost(msg.cost_usd);
      setRun((prev) =>
        prev ? {
          ...prev,
          status: "completed",
          progress_completed: msg.completed,
          progress_total: msg.total,
          cost_so_far_usd: msg.cost_usd,
          summary: msg.summary,
          finished_at: msg.timestamp,
        } : prev,
      );
      if (msg.customer) onCustomerUpdate(msg.customer);
      toast.success("Measurement complete. Moving to Stage 5.");
      onStageComplete();
    } else if (msg.type === "measurement_cancelled") {
      setRun((prev) =>
        prev ? {
          ...prev,
          status: "cancelled",
          progress_completed: msg.completed,
          progress_total: msg.total,
          cost_so_far_usd: msg.cost_usd,
          finished_at: msg.timestamp,
        } : prev,
      );
      toast.info("Run cancelled.");
    } else if (msg.type === "error") {
      setRun((prev) =>
        prev ? { ...prev, status: "failed", error_message: msg.message } : prev,
      );
      toast.error(msg.message || "Measurement failed");
    }
  }, [onCustomerUpdate, onStageComplete]);

  // Connection key: open WS while a run is alive (pending/running);
  // close it on terminal states. Changing run.id reconnects cleanly.
  const wsConnectionKey = useMemo(() => {
    if (!frameworkApproved || !run) return null;
    if (run.status !== "pending" && run.status !== "running") return null;
    return run.id;
  }, [frameworkApproved, run]);

  useResumableWs<MeasurementWsMessage>({
    connectionKey: wsConnectionKey,
    openWs: () =>
      openMeasurementWs({
        customerId: customer.id,
        onEvent: () => { /* hook overrides */ },
      }),
    onMessage: handleWsMessage,
    onClose: () => { void recheckIfMaybeFinished(); },
  });

  // Heartbeat watchdog: while a run is live, if no WS frame has arrived for
  // longer than the silence threshold, poll the run endpoint and reconcile to
  // the server (server always wins). This unsticks the "frozen at 816/1000
  // while the server says 1000/1000" case where the socket stays open but the
  // terminal frame never lands. Cheap (one GET) and self-limiting: on any live
  // frame the clock resets, so a healthy stream never triggers it.
  useEffect(() => {
    const live = run && (run.status === "pending" || run.status === "running");
    if (!frameworkApproved || !live) return;
    lastWsAtRef.current = Date.now(); // fresh clock when a run becomes live
    const SILENCE_MS = 15000;
    const timer = setInterval(() => {
      if (Date.now() - lastWsAtRef.current >= SILENCE_MS) {
        lastWsAtRef.current = Date.now(); // throttle: next poll after another silent window
        void recheckIfMaybeFinished();
      }
    }, 5000);
    return () => clearInterval(timer);
  }, [frameworkApproved, run?.id, run?.status, recheckIfMaybeFinished]);

  const handleStart = async (opts: { restart?: boolean } = {}) => {
    setStarting(true);
    try {
      const res = await startMeasurement(
        customer.id,
        {
          queries: batchConfig.queries,
          reps: batchConfig.reps,
          models: batchConfig.models,
          // BatchConfig (UI) uses versionMode; the API uses snake_case version_mode.
          // No hardcoded "baseline" fallback: that is what pinned a brand-new
          // client's first read to the imported baseline's GPT-5.4 even though
          // the server defaults such a client to current models. Undefined ->
          // the field is omitted and the server resolves the customer's own
          // default (measurement_version_policy).
          version_mode: batchConfig.versionMode ?? versionPolicy?.default_mode,
          model_overrides: batchConfig.modelOverrides,
          endpoint_overrides: batchConfig.endpointOverrides,
          // Per-lane grounding (Web vs Static). The picker stores the operator's
          // choice in webSearchOverrides; without this line the run silently ran
          // the catalog defaults while the Stage-4 buttons still read "Web"
          // (customer 13 run 29, customer 14 runs 30/31: ChatGPT/Claude/Gemini
          // shown web-grounded, collected static).
          web_search_overrides: batchConfig.webSearchOverrides,
          // The Stage-5 judge, pinned before the run starts. The auto-pipeline
          // reads it back off the stored batch_config and scores with it, so
          // the run is judged exactly once, by the judge chosen here.
          scorer_model_id: batchConfig.scorerModelId,
          // How that judge pass is SENT. Undefined = the server decides, which
          // is the batch API at half price for everything but Smoke
          // calibration (where an operator is waiting) and judges with no
          // batch transport.
          scoring_mode: batchConfig.scoringMode,
        },
        opts,
      );
      setRun(res.run);
      setLiveCompleted(0);
      setLiveTotal(res.run.progress_total);
      setLiveCost(0);
      setLivePct(0);
      // WS opens via useResumableWs as soon as wsConnectionKey reflects
      // the new pending/running run id.
    } catch (e: any) {
      toast.error(e?.message || "Failed to start measurement");
    } finally {
      setStarting(false);
    }
  };

  // ── Auto-start from parent ───────────────────────────────────────────
  useEffect(() => {
    if (!autoStart) return;
    if (!frameworkApproved) {
      toast.error("Approve the framework in Stage 3 before starting measurement.");
      return;
    }
    if (run && (run.status === "pending" || run.status === "running")) return;
    if (!calibrationGateOpen) {
      toast.error("Run the Smoke calibration and clear its flags before the full baseline.");
      return;
    }
    handleStart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart]);

  const handleCancel = async () => {
    if (!run) return;
    setCancelling(true);
    try {
      const res = await cancelMeasurement(run.id);
      // Consume the returned run: if the backend force-finalized an orphaned
      // run (no live worker), status is already "cancelled" and the UI flips
      // out of "Cancelling…" immediately instead of hanging forever.
      setRun((prev) => (prev ? { ...prev, ...res.run } : res.run));
      if (res.run.status === "cancelled") {
        toast.info("Run cancelled.");
      } else {
        toast.info("Cancellation requested — worker will stop at the next batch.");
      }
    } catch (e: any) {
      toast.error(e?.message || "Failed to cancel");
    } finally {
      setCancelling(false);
    }
  };

  const handleRerun = () => handleStart({ restart: true });

  // A calibration round revised or unlocked the framework (#138).
  const reloadCustomer = useCallback(() => {
    void getCustomer(customer.id).then(onCustomerUpdate).catch(() => undefined);
  }, [customer.id, onCustomerUpdate]);

  const handleResume = async () => {
    if (!run) return;
    setStarting(true);
    try {
      const res = await resumeMeasurement(customer.id, run.id);
      setRun(res.run);
      setLiveCompleted(res.run.progress_completed);
      setLiveTotal(res.run.progress_total);
      setLiveCost(res.run.cost_so_far_usd);
      setLivePct(
        res.run.progress_total > 0
          ? (res.run.progress_completed / res.run.progress_total) * 100
          : 0,
      );
      // useResumableWs picks the new run-id up automatically.
    } catch (e: any) {
      toast.error(e?.message || "Failed to resume measurement");
    } finally {
      setStarting(false);
    }
  };

  // Open the "Run a bigger battery" picker, seeded with the previous run's
  // shape so the user starts from what they already ran and just bumps it up.
  const handleOpenNewRun = () => {
    if (run?.batch_config) {
      const validModels = (run.batch_config.models ?? []).filter(
        (m): m is MeasurementModel =>
          (MEASUREMENT_MODELS as string[]).includes(m),
      );
      setBatchConfig({
        queries: toCount(run.batch_config.queries, totalQueries),
        reps: toCount(run.batch_config.reps, 2),
        models: validModels.length > 0 ? validModels : [...MEASUREMENT_DEFAULT_MODELS],
      });
    }
    setShowNewRun(true);
  };

  const handleStartNewRun = async () => {
    setShowNewRun(false);
    await handleStart({ restart: true });
  };

  // ═══════════════════════════════════════════════════════════════════════
  // FRAMEWORK NOT APPROVED — gate everything else
  // ═══════════════════════════════════════════════════════════════════════
  if (!frameworkApproved) {
    return (
      <Card title="Measurement run — locked">
        <div
          className="flex items-start gap-3 p-3 rounded-lg"
          style={{
            backgroundColor: "rgba(245,158,11,0.08)",
            border: "1px solid var(--chart-3)",
          }}
        >
          <Lock className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: "var(--chart-3)" }} />
          <div>
            <div style={{ fontSize: "var(--text-sm)", fontWeight: 600, color: "var(--foreground)" }}>
              Approve the framework first
            </div>
            <div style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)", marginTop: 4, lineHeight: 1.5 }}>
              Go to <strong>Stage 3 — Framework approval</strong> and click
              the <strong>Approve framework</strong> button. Once approved,
              this stage unlocks and you can configure and start a measurement run.
            </div>
          </div>
        </div>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card title="Measurement run">
        <div className="flex items-center gap-2 py-4">
          <Loader2 className="w-4 h-4 animate-spin" style={{ color: "var(--alpha-teal)" }} />
          <span style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)" }}>
            Checking measurement state…
          </span>
        </div>
      </Card>
    );
  }

  if (!run) {
    const calls = computeBatchCalls(batchConfig);
    return (
      <Card title="Measurement run">
        <SmokeCalibrationPanel
          customerId={customer.id}
          frameworkApproved={frameworkApproved}
          onGateChange={setCalibrationGateOpen}
          // Calibrate the SAME lanes the full baseline will run. Without this the
          // pre-flight dropped the picker's selection and silently ran the default
          // subset — an opt-in lane (aimode) never calibrated, an unselected lane
          // (gptweb) did — while the baseline below carried batchConfig verbatim.
          selection={batchConfig}
          onFrameworkChanged={reloadCustomer}
        />
        <div className="space-y-3">
          <BatchConfigPicker
            totalQueries={totalQueries}
            value={batchConfig}
            onChange={setBatchConfig}
            versionPins={versionPolicy?.pins}
            firstReadDefaults={versionPolicy?.first_read_defaults}
            versionPinned={versionPolicy?.pinned}
          />
          <p style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)" }}>
            The full baseline unlocks once Smoke calibration is <strong>PASS</strong> or its
            flags are acknowledged. {calls} call(s) — est. cost{" "}
            <strong>${computeBatchCost(batchConfig).toFixed(2)}</strong>. Page is
            safe to leave; you can come back any time.
          </p>
          <p style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
            Grounding this run will use: <strong>{fmtGroundingPlan(batchConfig)}</strong>
          </p>
          <button
            onClick={() => handleStart()}
            disabled={!calibrationGateOpen || starting}
            title={calibrationGateOpen ? undefined : "Run Smoke calibration first"}
            className="flex items-center gap-2 px-4 py-2 rounded-lg"
            style={{
              backgroundColor: calibrationGateOpen ? "var(--alpha-teal)" : "var(--muted)",
              color: calibrationGateOpen ? "white" : "var(--muted-foreground)",
              border: calibrationGateOpen ? "none" : "1px solid var(--border)",
              fontSize: "var(--text-sm)", fontWeight: 600,
              cursor: !calibrationGateOpen || starting ? "not-allowed" : "pointer",
              opacity: starting ? 0.6 : 1,
            }}
          >
            {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
            {calibrationGateOpen ? "Proceed to full baseline" : "Locked — calibrate first"}
          </button>
        </div>
      </Card>
    );
  }

  return (
    <Card title="Measurement run">
      <RunHeader run={run} />

      {(run.status === "pending" || run.status === "running") && (
        <ActiveRunPanel
          run={run}
          livePct={livePct}
          liveCompleted={liveCompleted}
          liveTotal={liveTotal}
          liveCost={liveCost}
          cancelling={cancelling}
          onCancel={handleCancel}
        />
      )}

      {run.status === "completed" && (
        <>
          <CompletedPanel run={run} onContinue={onStageComplete} />
          {/* A new run is a read, and every read needs its rubric and battery
              to have passed Smoke calibration (#138). */}
          {showNewRun && (
            <SmokeCalibrationPanel
              customerId={customer.id}
              frameworkApproved={frameworkApproved}
              selection={batchConfig}
              onFrameworkChanged={reloadCustomer}
            />
          )}
          <NewRunSection
            totalQueries={totalQueries}
            batchConfig={batchConfig}
            onBatchConfigChange={setBatchConfig}
            versionPins={versionPolicy?.pins}
            firstReadDefaults={versionPolicy?.first_read_defaults}
            versionPinned={versionPolicy?.pinned}
            showPicker={showNewRun}
            onOpen={handleOpenNewRun}
            onCancel={() => setShowNewRun(false)}
            onStart={handleStartNewRun}
            starting={starting}
          />
        </>
      )}

      {run.status === "cancelled" && (
        <>
          <TerminalPanel
            run={run} title="Run cancelled" tone="warn"
            onRerun={handleRerun} rerunDisabled={starting}
            onResume={handleResume}
          />
          <NewRunSection
            totalQueries={totalQueries}
            batchConfig={batchConfig}
            onBatchConfigChange={setBatchConfig}
            versionPins={versionPolicy?.pins}
            firstReadDefaults={versionPolicy?.first_read_defaults}
            versionPinned={versionPolicy?.pinned}
            showPicker={showNewRun}
            onOpen={handleOpenNewRun}
            onCancel={() => setShowNewRun(false)}
            onStart={handleStartNewRun}
            starting={starting}
          />
        </>
      )}

      {run.status === "failed" && (
        <>
          <TerminalPanel
            run={run} title="Run failed" tone="error"
            onRerun={handleRerun} rerunDisabled={starting}
            onResume={handleResume}
          />
          <NewRunSection
            totalQueries={totalQueries}
            batchConfig={batchConfig}
            onBatchConfigChange={setBatchConfig}
            versionPins={versionPolicy?.pins}
            firstReadDefaults={versionPolicy?.first_read_defaults}
            versionPinned={versionPolicy?.pinned}
            showPicker={showNewRun}
            onOpen={handleOpenNewRun}
            onCancel={() => setShowNewRun(false)}
            onStart={handleStartNewRun}
            starting={starting}
          />
        </>
      )}
    </Card>
  );
}

function NewRunSection({
  totalQueries,
  batchConfig,
  onBatchConfigChange,
  versionPins,
  firstReadDefaults,
  versionPinned,
  showPicker,
  onOpen,
  onCancel,
  onStart,
  starting,
}: {
  totalQueries: number;
  batchConfig: BatchConfig;
  // Passed straight through to BatchConfigPicker, which needs the updater form
  // so a handler touching two fields composes instead of clobbering.
  onBatchConfigChange: Dispatch<SetStateAction<BatchConfig>>;
  // The customer's standing per-lane version pins, so a re-read's picker names
  // the versions it will actually run (see BatchConfigPickerProps).
  versionPins?: Record<string, string> | null;
  firstReadDefaults?: Record<string, string> | null;
  versionPinned?: boolean;
  showPicker: boolean;
  onOpen: () => void;
  onCancel: () => void;
  onStart: () => void;
  starting: boolean;
}) {
  if (!showPicker) {
    return (
      <div className="mt-4 pt-4" style={{ borderTop: "1px solid var(--border)" }}>
        <button
          onClick={onOpen}
          disabled={starting}
          className="flex items-center gap-2 px-4 py-2 rounded-lg"
          style={{
            backgroundColor: "transparent",
            color: "var(--alpha-teal)",
            border: "1px solid var(--alpha-teal)",
            fontSize: "var(--text-sm)",
            fontWeight: 600,
            cursor: starting ? "not-allowed" : "pointer",
            opacity: starting ? 0.6 : 1,
          }}
        >
          <Rocket className="w-4 h-4" />
          Run a bigger battery
        </button>
      </div>
    );
  }

  const calls = computeBatchCalls(batchConfig);
  const cost = computeBatchCost(batchConfig);

  return (
    <div className="mt-4 pt-4 space-y-3" style={{ borderTop: "1px solid var(--border)" }}>
      <BatchConfigPicker
        totalQueries={totalQueries}
        value={batchConfig}
        onChange={onBatchConfigChange}
        versionPins={versionPins}
        firstReadDefaults={firstReadDefaults}
        versionPinned={versionPinned}
      />
      <p style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
        Grounding this run will use: <strong>{fmtGroundingPlan(batchConfig)}</strong>
      </p>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
          Starting a new run will replace the previous run's pointer. Partial
          data from past runs stays in the database.
        </span>
        <div className="flex items-center gap-2">
          <button
            onClick={onCancel}
            disabled={starting}
            className="px-3 py-1.5 rounded-md"
            style={{
              backgroundColor: "transparent",
              color: "var(--muted-foreground)",
              border: "1px solid var(--border)",
              fontSize: "var(--text-xs)",
              fontWeight: 500,
              cursor: starting ? "not-allowed" : "pointer",
            }}
          >
            Cancel
          </button>
          <button
            onClick={onStart}
            disabled={starting}
            className="flex items-center gap-2 px-4 py-2 rounded-lg"
            style={{
              backgroundColor: "var(--alpha-teal)", color: "white",
              fontSize: "var(--text-sm)", fontWeight: 600,
              border: "none",
              cursor: starting ? "not-allowed" : "pointer",
              opacity: starting ? 0.6 : 1,
            }}
          >
            {starting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Rocket className="w-4 h-4" />
            )}
            Start new run · {calls} call{calls === 1 ? "" : "s"} · ${cost.toFixed(2)}
          </button>
        </div>
      </div>
    </div>
  );
}

function RunHeader({ run }: { run: MeasurementRun }) {
  const cfg = run.batch_config;
  const started = run.started_at ? new Date(run.started_at).toLocaleString() : "—";
  // The server-resolved map stored on the run — the grounding regime that
  // actually executed, not the picker state.
  const grounding = fmtGroundingActual(cfg?.web_search);
  return (
    <div
      className="p-3 rounded-lg mb-3"
      style={{
        backgroundColor: "var(--muted)",
        border: "1px solid var(--border)",
        fontSize: "var(--text-xs)",
      }}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <span style={{ color: "var(--muted-foreground)" }}>Scope: </span>
          <strong style={{ color: "var(--foreground)" }}>
            {toCount(cfg?.queries, 0)} queries × {toCount(cfg?.reps, 0)} reps × {cfg?.models?.length || 0} models
          </strong>
          <span style={{ color: "var(--muted-foreground)" }}> ({run.progress_total} calls)</span>
          {grounding && (
            <div style={{ color: "var(--muted-foreground)", marginTop: 2 }}>
              Grounding: {grounding}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1.5" style={{ color: "var(--muted-foreground)" }}>
          <Clock className="w-3 h-3" />
          <span>{run.run_name?.trim() || `Run #${run.run_sequence_number ?? run.id}`} · started {started}</span>
        </div>
      </div>
    </div>
  );
}

function ActiveRunPanel({
  run, livePct, liveCompleted, liveTotal, liveCost, cancelling, onCancel,
}: {
  run: MeasurementRun;
  livePct: number; liveCompleted: number; liveTotal: number; liveCost: number;
  cancelling: boolean; onCancel: () => void;
}) {
  const isCancelling = run.cancellation_requested;
  return (
    <div className="space-y-3">
      <div>
        <div className="flex items-center justify-between mb-1">
          <span style={{ fontSize: "var(--text-sm)", color: "var(--foreground)", fontWeight: 500 }}>
            {liveCompleted} / {liveTotal} calls
          </span>
          <span style={{ fontSize: "var(--text-sm)", color: "var(--alpha-teal)", fontWeight: 600 }}>
            {livePct.toFixed(1)}%
          </span>
        </div>
        <div className="w-full h-2 rounded-full overflow-hidden" style={{ backgroundColor: "var(--muted)" }}>
          <div
            className="h-full transition-all duration-200"
            style={{
              width: `${livePct}%`,
              backgroundColor: isCancelling ? "var(--chart-3)" : "var(--alpha-teal)",
            }}
          />
        </div>
      </div>

      <div className="flex items-center justify-between gap-2">
        <span style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
          Live cost so far:{" "}
          <strong style={{ color: "var(--foreground)" }}>${liveCost.toFixed(2)}</strong>
        </span>
        <button
          onClick={onCancel}
          disabled={cancelling || isCancelling}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md"
          style={{
            backgroundColor: "transparent",
            color: "var(--infrared-coral)",
            border: "1px solid var(--infrared-coral)",
            fontSize: "var(--text-xs)",
            fontWeight: 500,
            cursor: cancelling || isCancelling ? "not-allowed" : "pointer",
            opacity: cancelling || isCancelling ? 0.6 : 1,
          }}
        >
          <StopCircle className="w-3.5 h-3.5" />
          {isCancelling ? "Cancelling…" : "Cancel run"}
        </button>
      </div>

      {isCancelling && (
        <div
          className="flex items-start gap-2 p-2 rounded-md"
          style={{ backgroundColor: "rgba(245,158,11,0.08)", border: "1px solid var(--chart-3)" }}
        >
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: "var(--chart-3)" }} />
          <span style={{ fontSize: "var(--text-xs)", color: "var(--foreground)" }}>
            Cancellation requested — worker will stop at the next batch checkpoint
            (~30s). Partial data is being preserved.
          </span>
        </div>
      )}
    </div>
  );
}

function CompletedPanel({
  run,
  onContinue,
}: {
  run: MeasurementRun;
  onContinue: () => void;
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2" style={{ color: "var(--chart-3)" }}>
        <Check className="w-5 h-5" />
        <span style={{ fontSize: "var(--text-sm)", fontWeight: 600 }}>Run complete</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Stat label="Calls" value={String(run.summary?.total_calls ?? run.progress_total)} />
        <Stat label="Successful" value={String(run.summary?.successful ?? "—")} />
        <Stat label="Failed" value={String(run.summary?.failed ?? 0)} />
        <Stat label="Cost (USD)" value={`$${(run.summary?.cost_usd ?? run.cost_so_far_usd).toFixed(2)}`} />
      </div>
      <AiModeRetryNote retries={run.summary?.aimode_retries} />
      <div className="flex items-center justify-between gap-2 flex-wrap pt-1">
        <p style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)", margin: 0 }}>
          Or expand the panel below to scale the battery up and re-run.
        </p>
        <button
          onClick={onContinue}
          className="flex items-center gap-2 px-4 py-2 rounded-lg"
          style={{
            backgroundColor: "var(--alpha-teal)", color: "white",
            fontSize: "var(--text-sm)", fontWeight: 600,
            border: "none", cursor: "pointer",
          }}
        >
          Continue to Stage 5
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function TerminalPanel({
  run, title, tone, onRerun, rerunDisabled, onResume,
}: {
  run: MeasurementRun;
  title: string; tone: "warn" | "error";
  onRerun: () => void; rerunDisabled: boolean;
  onResume?: () => void;
}) {
  const color = tone === "error" ? "var(--infrared-coral)" : "var(--chart-3)";
  const bg = tone === "error" ? "rgba(220,38,38,0.08)" : "rgba(245,158,11,0.08)";

  const canResume =
    !!onResume &&
    run.progress_completed > 0 &&
    run.progress_completed < run.progress_total;
  const remaining = run.progress_total - run.progress_completed;

  return (
    <div className="space-y-3">
      <div className="flex items-start gap-2 p-3 rounded-lg" style={{ backgroundColor: bg, border: `1px solid ${color}` }}>
        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color }} />
        <div>
          <div style={{ fontSize: "var(--text-sm)", fontWeight: 600, color }}>{title}</div>
          {run.error_message && (
            <div style={{ fontSize: "var(--text-xs)", color: "var(--foreground)", marginTop: 4 }}>
              {run.error_message}
            </div>
          )}
          <div style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)", marginTop: 4 }}>
            Stopped at {run.progress_completed} / {run.progress_total} calls.
            Cost so far: ${run.cost_so_far_usd.toFixed(2)}. Partial data preserved.
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {canResume && (
          <button
            onClick={onResume}
            disabled={rerunDisabled}
            className="flex items-center gap-2 px-4 py-2 rounded-lg"
            style={{
              backgroundColor: "var(--alpha-teal)", color: "white",
              fontSize: "var(--text-sm)", fontWeight: 600,
              border: "none",
              cursor: rerunDisabled ? "not-allowed" : "pointer",
              opacity: rerunDisabled ? 0.6 : 1,
            }}
          >
            <Play className="w-4 h-4" />
            Resume from {run.progress_completed} / {run.progress_total} ({remaining} left)
          </button>
        )}
        <button
          onClick={onRerun}
          disabled={rerunDisabled}
          className="flex items-center gap-2 px-4 py-2 rounded-lg"
          style={{
            backgroundColor: canResume ? "transparent" : "var(--alpha-teal)",
            color: canResume ? "var(--alpha-teal)" : "white",
            border: canResume ? "1px solid var(--alpha-teal)" : "none",
            fontSize: "var(--text-sm)", fontWeight: 600,
            cursor: rerunDisabled ? "not-allowed" : "pointer",
            opacity: rerunDisabled ? 0.6 : 1,
          }}
        >
          <RotateCcw className="w-4 h-4" />
          {canResume ? "Restart from scratch" : "Re-run measurement"}
        </button>
      </div>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl p-4 md:p-5" style={{ backgroundColor: "var(--card)", border: "1px solid var(--border)" }}>
      <div className="mb-4">
        <h3 style={{ fontSize: "var(--text-base)", fontWeight: 600, color: "var(--foreground)", margin: 0 }}>
          {title}
        </h3>
      </div>
      {children}
    </div>
  );
}

// Google AI Mode sometimes returns a billed answer container with nothing
// captured in it. Those rows are retried, and a row still empty at the cap is
// recorded as a FAILED measurement and left out of the client's rates (#108).
// This is where an operator finds out that happened — silently excluding rows
// would make the lane's call count and its rate count disagree with no
// explanation on screen.
function AiModeRetryNote({ retries }: { retries?: AiModeRetrySummary }) {
  if (!retries || !retries.aimode_rows) return null;
  // A run that retried nothing and excluded nothing has nothing to report:
  // the healthy case should not add a line to every completed run.
  if (!retries.retried_rows && !retries.failed_rows) return null;

  const budgetStopped = retries.stopped?.budget ?? 0;
  const tone = retries.failed_rows ? "var(--chart-3)" : "var(--muted-foreground)";

  return (
    <div
      className="flex items-start gap-2 p-2 rounded-md"
      style={{
        backgroundColor: retries.failed_rows ? "rgba(245,158,11,0.08)" : "var(--muted)",
        border: `1px solid ${retries.failed_rows ? "var(--chart-3)" : "var(--border)"}`,
      }}
    >
      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: tone }} />
      <span style={{ fontSize: "var(--text-xs)", color: "var(--foreground)" }}>
        <strong>Google AI Mode:</strong>{" "}
        {retries.retried_rows > 0 && (
          <>
            {retries.retried_rows} empty{" "}
            {retries.retried_rows === 1 ? "row" : "rows"} retried (
            {retries.retry_attempts} extra{" "}
            {retries.retry_attempts === 1 ? "call" : "calls"}, $
            {retries.retry_cost_usd.toFixed(4)}).{" "}
          </>
        )}
        {retries.failed_rows > 0 ? (
          <>
            {retries.failed_rows} still returned no answer and{" "}
            {retries.failed_rows === 1 ? "is" : "are"} recorded as failed —
            excluded from scoring and from this brand&apos;s rates rather than
            counted as &ldquo;not mentioned&rdquo;.
          </>
        ) : (
          <>Every empty row was filled by a retry.</>
        )}
        {budgetStopped > 0 && (
          <>
            {" "}
            <strong>
              The run&apos;s retry budget ran out, leaving {budgetStopped}{" "}
              {budgetStopped === 1 ? "row" : "rows"} un-retried.
            </strong>
          </>
        )}
      </span>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="p-3 rounded-lg" style={{ backgroundColor: "var(--muted)", border: "1px solid var(--border)" }}>
      <div style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)", marginBottom: 2, fontWeight: 500 }}>
        {label}
      </div>
      <div style={{ fontSize: "var(--text-lg)", fontWeight: 700, color: "var(--foreground)" }}>
        {value}
      </div>
    </div>
  );
}